import React, { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import PhysicianInvitePhysician from '@medflex/components/stories/PhysicianInvitePhysician';
import PermanentLink from '@medflex/components/stories/PermanentLink';
import { showAlertToast, showSuccessToast } from '@medflex/components/stories/Toast';
import { TRACKING_ACTIONS, TRACKING_ELEMENT_TYPES, trackEvent } from '@medflex/utils/Tracking';
import { dayjs } from '@medflex/shared-utils';
import { SubscriptionStatus } from '@medflex/shared-constants';
import useUserProfile from '../hooks/useUserProfile';
import usePhysicianInvitations from '../hooks/usePhysicianInvitations';
import useModal from '../hooks/useModal';
import useLocationSearchParams from '../../hooks/useLocationSearchParams';
import useSubscription from '../hooks/useSubscription';

const PhysicianInvitePhysicianModule = () => {
  const { userProfile } = useUserProfile();
  const isComposing = useSelector(state => state.loading.effects.physicianInvitation.create);
  const { pending: invitation, create, cleanupPending } = usePhysicianInvitations();
  const { t } = useTranslation('physician');
  const history = useHistory();
  const { close, open } = useModal();
  const { modal } = useLocationSearchParams();
  const { contractStatus, trialEndedAt } = useSubscription();

  const canSendSms =
    contractStatus === SubscriptionStatus.ACTIVE &&
    (!trialEndedAt || dayjs().isAfter(dayjs(trialEndedAt)));

  const closeModal = useCallback(() => {
    // there are (04.2021) two ways to reach this modal
    if (!modal) return history.push('/');
    return close({ modal: 'invitePhysician' });
  }, [close, history, modal]);

  useEffect(() => {
    if (invitation.email || invitation.mobileNumber) {
      closeModal();
      showSuccessToast(
        t('toasts.physician_invite.success.title', {
          medium: invitation.email || invitation.mobileNumber,
        }),
        <span>
          <button
            type="button"
            className="text-blue-500"
            onClick={() => open({ modal: 'invitePhysician' })}
          >
            {t('toasts.physician_invite.success.repeat')}
          </button>
        </span>,
        { toastId: 'physicianInviteSuccess' },
      );
    }
    if (invitation.isAlreadyContact) {
      showAlertToast(t('toasts.physician_invite.already_contact'), null, {
        toastId: 'physicianInviteFailed',
      });
      closeModal();
    }
  }, [invitation, closeModal, open, t]);

  useEffect(
    () => () => {
      cleanupPending();
    },
    [cleanupPending],
  );

  const handleSubmit = async formData => {
    try {
      await create(formData);
      trackEvent({
        zone: 'PhysicianInvitePhysicianModule',
        action: TRACKING_ACTIONS.SUBMIT,
        elementType: TRACKING_ELEMENT_TYPES.FORM,
        actionText: `invite by ${formData.email ? 'email' : 'mobileNumber'}`,
      });
    } catch (e) {
      showAlertToast(
        t('toasts.physician_invite.alert_toast.title'),
        t('toasts.physician_invite.alert_toast.text'),
        { toastId: 'invitePhysicianFailed' },
      );
    }
  };

  const handleCancel = event => {
    trackEvent(event, {
      zone: 'PhysicianInvitePhysicianModule',
      actionText: TRACKING_ACTIONS.CLOSE,
    });
    closeModal();
  };

  const handleCopied = event => {
    showSuccessToast(t('physician:toasts.permanent_link.copied'), null, {
      toastId: 'PermanentLinkCopySuccess',
    });
    trackEvent(event, {
      zone: 'PhysicianInvitePhysicianModule',
      elementType: 'form',
      actionText: 'invite by Copied permanentLink',
    });
  };

  const handleShared = event => {
    trackEvent(event, {
      zone: 'PhysicianInvitePhysicianModule',
    });
  };

  const handleClickGenerateNew = event => {
    trackEvent(event, {
      zone: 'PhysicianInvitePhysicianModule',
    });
    open({ modal: 'newPermanentLink', type: 'physician' });
  };

  return (
    <PhysicianInvitePhysician onCancel={handleCancel}>
      <PermanentLink
        className="mb-4"
        uri={userProfile.attributes.permanentInvitationLink}
        onCopied={handleCopied}
        title={t('messenger:PhysicianInvitePhysicianModule.PermanentLink.title')}
        onShared={handleShared}
        onClickGenerateNew={handleClickGenerateNew}
      />
      <div className="flex items-center space-x-4 mb-4">
        <hr className="flex-grow" />
        <div className="font-semibold text-slate-600">{t('components:PermanentLink.or')}</div>
        <hr className="flex-grow" />
      </div>
      <PhysicianInvitePhysician.InvitePhysicianForm
        isComposing={isComposing}
        onSubmit={handleSubmit}
        sender={userProfile.attributes.fullNameWithTitle}
        canSendSms={canSendSms}
      />
    </PhysicianInvitePhysician>
  );
};

export default PhysicianInvitePhysicianModule;
